import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import VillaCard from "@/components/villa/VillaCard";
import Eyebrow from "@/components/common/Eyebrow";
import { CATEGORIES } from "@/constants/categories";
import { SlidersHorizontal } from "lucide-react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Slider from "@mui/material/Slider";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";

function FiltersPanel({ filters, setFilters, priceRange, setPriceRange }) {
  const toggleCat = (id) => {
    setFilters(f => ({ ...f, categories: f.categories.includes(id) ? f.categories.filter(c=>c!==id) : [...f.categories, id] }));
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }} data-testid="filters-panel">
      <Box>
        <Eyebrow sx={{ mb: 2 }}>Price per night</Eyebrow>
        <Slider
          min={5000}
          max={50000}
          step={1000}
          value={priceRange}
          onChange={(e, val) => setPriceRange(val)}
          data-testid="price-slider"
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12, mt: 1.5, color: "text.secondary" }}>
          <span>₹{priceRange[0].toLocaleString()}</span>
          <span>₹{priceRange[1].toLocaleString()}</span>
        </Box>
      </Box>
      <Box>
        <Eyebrow sx={{ mb: 2 }}>Collection</Eyebrow>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {CATEGORIES.map(c => {
            const active = filters.categories.includes(c.id);
            return (
              <Box
                key={c.id}
                component="button"
                type="button"
                onClick={() => toggleCat(c.id)}
                data-testid={`filter-cat-${c.id}`}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.9, borderRadius: 2.5,
                  border: 1, borderColor: active ? "text.primary" : "divider",
                  bgcolor: active ? "text.primary" : "transparent",
                  color: active ? "background.default" : "text.primary",
                  cursor: "pointer", fontSize: 13, fontFamily: "inherit",
                  "&:hover": { borderColor: "text.primary" },
                }}
              >
                <c.Icon size={13} /> {c.label}
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box>
        <Eyebrow sx={{ mb: 2 }}>Features</Eyebrow>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Checkbox
                checked={filters.instant_book}
                onChange={(e) => setFilters(f=>({...f, instant_book: e.target.checked}))}
                inputProps={{ "data-testid": "filter-instant" }}
                size="small"
              />
            }
            label={<Typography variant="body2">Instant booking</Typography>}
          />
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Checkbox
                checked={filters.pet_friendly}
                onChange={(e) => setFilters(f=>({...f, pet_friendly: e.target.checked}))}
                inputProps={{ "data-testid": "filter-pet" }}
                size="small"
              />
            }
            label={<Typography variant="body2">Pet friendly</Typography>}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default function Search() {
  const [params] = useSearchParams();
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [priceRange, setPriceRange] = useState([5000, 50000]);
  const [filters, setFilters] = useState({
    categories: params.get("category") ? [params.get("category")] : [],
    instant_book: false,
    pet_friendly: false,
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const destination = params.get("destination") || "";
  const guests = params.get("guests");

  useEffect(() => {
    setFilters(f => ({ ...f, categories: params.get("category") ? [params.get("category")] : [] }));
  }, [params]);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (destination) q.set("destination", destination);
    if (guests) q.set("guests", guests);
    q.set("min_price", priceRange[0]);
    q.set("max_price", priceRange[1]);
    if (filters.instant_book) q.set("instant_book", "true");
    if (filters.pet_friendly) q.set("pet_friendly", "true");
    q.set("sort", sort);
    if (filters.categories.length === 1) q.set("category", filters.categories[0]);
    api.get(`/villas?${q.toString()}`).then(r => {
      let list = r.data;
      if (filters.categories.length > 1) {
        list = list.filter(v => filters.categories.some(c => v.categories?.includes(c)));
      }
      setVillas(list);
    }).finally(() => setLoading(false));
  }, [destination, guests, priceRange, filters, sort]);

  return (
    <Box data-testid="search-page">
      <Box sx={{ maxWidth: 1500, mx: "auto", px: { xs: 3, lg: 6 }, pt: 5, pb: 12 }}>
        <Box sx={{ mb: 4, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Eyebrow sx={{ mb: 1 }}>Stays</Eyebrow>
            <Typography
              className="font-serif-display"
              sx={{ fontSize: { xs: "1.75rem", md: "2.25rem" }, letterSpacing: "-0.01em", lineHeight: 1.1 }}
            >
              {destination ? destination : "All villas"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="results-count">
              {villas.length} private residences
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              onClick={() => setMobileFiltersOpen(true)}
              data-testid="mobile-filters-btn"
              variant="outlined"
              startIcon={<SlidersHorizontal size={14} />}
              sx={{
                display: { xs: "inline-flex", lg: "none" },
                fontSize: 14,
                borderRadius: 999,
                textTransform: "none",
              }}
            >
              Filters
            </Button>
            <Drawer anchor="left" open={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)}>
              <Box sx={{ width: 320, overflowY: "auto", p: 3 }} role="presentation">
                <Typography className="font-serif-display" sx={{ fontSize: "1.5rem" }}>Refine</Typography>
                <Box sx={{ mt: 4 }}>
                  <FiltersPanel filters={filters} setFilters={setFilters} priceRange={priceRange} setPriceRange={setPriceRange} />
                </Box>
              </Box>
            </Drawer>
            <FormControl size="small">
              <Select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="sort-select"
                sx={{ width: 176, borderRadius: 999 }}
              >
                <MenuItem value="featured">Featured</MenuItem>
                <MenuItem value="price_asc">Price: Low to High</MenuItem>
                <MenuItem value="price_desc">Price: High to Low</MenuItem>
                <MenuItem value="rating">Highest rated</MenuItem>
                <MenuItem value="newest">Newest</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { lg: "260px 1fr" }, gap: 6 }}>
          <Box component="aside" sx={{ display: { xs: "none", lg: "block" }, position: "sticky", top: 176, alignSelf: "start" }}>
            <FiltersPanel filters={filters} setFilters={setFilters} priceRange={priceRange} setPriceRange={setPriceRange} />
          </Box>
          <Box>
            {loading ? (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: { xs: 3, md: 4 } }}>
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} variant="rounded" sx={{ aspectRatio: "1 / 1", width: "100%" }} />
                ))}
              </Box>
            ) : villas.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 16 }} data-testid="no-results">
                <Typography className="font-serif-display" sx={{ fontSize: "1.875rem", mb: 1.5 }}>
                  Nothing matches just yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your dates or filters.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: { xs: 3, md: 4 } }}
                data-testid="villa-grid"
              >
                {villas.map(v => <VillaCard key={v.id} villa={v} />)}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
